import {fetchRouteSegmentNumber} from '../apis/api-segments.ts';
import {fetchPlacesByRefid} from '../apis/api-places.ts';

import type {Bag} from '../urlHandling/urlNormalizer.ts';
import {
  landUrlFragment,
  overlayDirectUrlFragment,
  routeUrlFragment,
  segmentUrlFragment,
} from '../urlHandling/urlParams.ts';
import type {AdditionalLayerData} from '../apis/api-poi.ts';
import {fetchAdditionalLayerByRefid} from '../apis/api-poi.ts';
import {isAdditionalLayerType} from '../layers/layerHelpers.ts';

/**
 *
 * @param bag
 * @param url
 * @returns
 */
export async function resolvePartialUrl(
  bag: Bag,
  url: string,
): Promise<URL | void> {
  // get the route and segment number from the segment id and return the canonical URL
  if (bag.segmentWebdbId) {
    const routeSegmentNumber = await fetchRouteSegmentNumber(
      bag.segmentWebdbId,
    );
    if (routeSegmentNumber) {
      const parts = [
        bag.lang,
        landUrlFragment[bag.land][bag.lang],
        `${routeUrlFragment[bag.lang]}-${routeSegmentNumber.routeNumber}`,
      ];
      if (routeSegmentNumber.segmentNumber !== 0) {
        parts.push(
          `${segmentUrlFragment[bag.lang]}-${routeSegmentNumber.segmentNumber}`,
        );
      }
      const normalizedURL = new URL(url);
      normalizedURL.pathname = parts.join('/');
      return normalizedURL;
    }
  }

  let parts: string[] | undefined = undefined;

  if (bag.placeWebdbId) {
    const places = await fetchPlacesByRefid(bag.placeWebdbId, bag.lang);
    parts = [
      bag.lang,
      `${overlayDirectUrlFragment.place[bag.lang]}-${places[0].id}`,
    ];
  }
  if (bag.additionalLayerWebdbId && isAdditionalLayerType(bag.overlayType)) {
    const additionalLayers =
      await fetchAdditionalLayerByRefid<AdditionalLayerData>(
        [bag.additionalLayerWebdbId],
        bag.overlayType,
        bag.lang,
      );
    if (!additionalLayers || !additionalLayers[0]) return undefined;
    parts = [
      bag.lang,
      `${overlayDirectUrlFragment[bag.overlayType][bag.lang]}-${additionalLayers[0].id}`,
    ];
  }

  if (bag.sightseeingId) {
    parts = [
      bag.lang,
      `${overlayDirectUrlFragment.sightseeing[bag.lang]}-${bag.sightseeingId}`,
    ];
  }

  if (bag.stationId) {
    parts = [
      bag.lang,
      `${overlayDirectUrlFragment.station[bag.lang]}-${bag.stationId}`,
    ];
  }

  if (bag.slowupId) {
    parts = [
      bag.lang,
      `${overlayDirectUrlFragment.slowup[bag.lang]}-${bag.slowupId}`,
    ];
  }

  if (bag.detourId) {
    parts = [
      bag.lang,
      `${overlayDirectUrlFragment.detour[bag.lang]}-${bag.detourId}`,
    ];
  }

  if (parts && parts.length) {
    const normalizedURL = new URL(url);
    normalizedURL.pathname = parts.join('/');
    return normalizedURL;
  }

  if (bag.trackId || bag.recordedTrackId) {
    const normalizedURL = new URL(url);
    if (bag.trackId) {
      normalizedURL.pathname = `/${bag.lang}/tour/${bag.trackId}`;
    } else {
      normalizedURL.pathname = `/${bag.lang}/recordedtrack/${bag.recordedTrackId}`;
    }
    return normalizedURL;
  }
  return undefined;
}
